import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { ConfigProvider } from '@config/provider';
import { loadRuntimeConfig, createDefaultConfig } from '@config/runtime';
import { createMockAwareFetch, shouldEnableMocks } from '@mocks/mockFetch';
import { AuthSessionProvider } from '@app/context/auth-session';
import { AuthFlowProvider } from '@app/context/auth-flow';
import { ThemeProvider } from '@app/context/theme';

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Failed to bootstrap UI: #root element is missing');
}

const root = ReactDOM.createRoot(rootElement);

async function bootstrap() {
  const fallbackConfig = createDefaultConfig();
  const loadedConfig = await loadRuntimeConfig(fallbackConfig);
  // Opt-in local design review. Never enables mock authentication in a production build.
  const config = import.meta.env.DEV && import.meta.env.VITE_RUNBOOK_SCHEDULER_MOCK === 'true'
    ? { ...loadedConfig, mockRoutes: ['auth', 'orgs', 'org-console', 'dashboards', 'signals'] }
    : loadedConfig;

  const effectiveMockRoutes = config.mockRoutes;
  const enableMocks = shouldEnableMocks(effectiveMockRoutes);

  if (enableMocks) {
    const nextFetch = createMockAwareFetch(effectiveMockRoutes, window.fetch.bind(window));
    window.fetch = nextFetch;
  }

  if (import.meta.env.DEV && import.meta.env.VITE_RUNBOOK_SCHEDULER_MOCK === 'true') {
    const { createRunbookDemoFetch } = await import('@mocks/routes/runbooks');
    window.fetch = createRunbookDemoFetch(window.fetch.bind(window));
  }

  const configWithMocks = enableMocks ? { ...config, mockRoutes: effectiveMockRoutes } : config;
  if (configWithMocks.environment === 'local') {
    console.info('[runtime-config] loaded', configWithMocks);
    console.info('[mocks] enabled', { enabled: enableMocks, routes: effectiveMockRoutes });
  }

  root.render(
    <React.StrictMode>
      <ConfigProvider value={configWithMocks}>
        <ThemeProvider>
          <AuthSessionProvider>
            <AuthFlowProvider>
              <App />
            </AuthFlowProvider>
          </AuthSessionProvider>
        </ThemeProvider>
      </ConfigProvider>
    </React.StrictMode>
  );
}

bootstrap().catch((error) => {
  console.error('Failed to bootstrap REAP UI', error);
  root.render(
    <main className="startup-error">
      <h1>REAP UI failed to load</h1>
      <p>{error.message}</p>
    </main>
  );
});
